import React, { useState, useRef, useEffect, useCallback } from "react";
import {
    FiPlus, FiTrash2, FiCamera, FiUpload, FiInfo,
    FiUser, FiPhone, FiCreditCard, FiShield,
    FiCheckCircle, FiX, FiRefreshCw, FiTag, FiLock,
} from "react-icons/fi";
import { toast } from "react-toastify";
import axiosInstance from "../../utils/api";
import Navbar from "../../components/Navbar";

const BADGE_TYPES = [
    { value: "build_up", label: "Build Up" },
    { value: "show_days", label: "Show Days" },
    { value: "teardown", label: "Tear Down" },
];

// How long to wait after the last keystroke/photo change before firing
// a verification request. Long enough to cover normal typing speed,
// short enough to still feel responsive.
const VERIFY_DEBOUNCE_MS = 700;

const CameraModal = ({ onCapture, onClose }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [captured, setCaptured] = useState(null);
    const [facingMode, setFacingMode] = useState("environment");
    const [error, setError] = useState(null);

    const startStream = useCallback(async (mode) => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        setReady(false);
        setCaptured(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: mode, width: { ideal: 1280 }, height: { ideal: 720 } },
                audio: false,
            });
            streamRef.current = stream;
            const video = videoRef.current;
            if (!video) return;
            video.srcObject = stream;
            video.onloadedmetadata = () => {
                video.play()
                    .then(() => setReady(true))
                    .catch(() => setReady(true));
            };
            setError(null);
        } catch {
            setError("Impossible d'accéder à la caméra.\nVérifiez les permissions du navigateur.");
        }
    }, []);

    useEffect(() => {
        startStream(facingMode);
        return () => { streamRef.current?.getTracks().forEach((t) => t.stop()); };
    }, []);

    const flipCamera = () => {
        const next = facingMode === "environment" ? "user" : "environment";
        setFacingMode(next);
        startStream(next);
    };

    const capture = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext("2d").drawImage(video, 0, 0);
        canvas.toBlob(
            (blob) => {
                const file = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" });
                setCaptured(file);
            },
            "image/jpeg", 0.92
        );
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/75 backdrop-blur-sm">
            <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl overflow-hidden shadow-2xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-xl bg-blue-900/10 text-blue-900 flex items-center justify-center">
                            <FiCamera size={16} />
                        </div>
                        <span className="font-semibold text-slate-800 text-sm">Prendre une photo</span>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center justify-center">
                        <FiX size={16} />
                    </button>
                </div>

                <div className="relative bg-black" style={{ aspectRatio: "16/9" }}>
                    {error ? (
                        <div className="absolute inset-0 flex items-center justify-center p-6">
                            <p className="text-white/70 text-sm text-center whitespace-pre-line">{error}</p>
                        </div>
                    ) : (
                        <>
                            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted style={{ display: captured ? "none" : "block" }} />
                            {captured && <img src={URL.createObjectURL(captured)} alt="capture" className="w-full h-full object-cover" />}
                            {!captured && ready && (
                                <>
                                    <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-white/60 rounded-tl-md pointer-events-none" />
                                    <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-white/60 rounded-tr-md pointer-events-none" />
                                    <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-white/60 rounded-bl-md pointer-events-none" />
                                    <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-white/60 rounded-br-md pointer-events-none" />
                                </>
                            )}
                        </>
                    )}
                </div>
                <canvas ref={canvasRef} className="hidden" />

                <div className="px-4 py-4 flex items-center gap-3">
                    {!captured ? (
                        <>
                            <button onClick={flipCamera} className="w-11 h-11 shrink-0 rounded-2xl border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center justify-center" title="Changer caméra">
                                <FiRefreshCw size={17} />
                            </button>
                            <button onClick={capture} disabled={!ready} className="flex-1 h-11 rounded-2xl bg-blue-900 hover:bg-blue-800 disabled:opacity-40 text-white font-semibold flex items-center justify-center gap-2 text-sm transition shadow-lg shadow-blue-900/20">
                                <FiCamera size={16} /> Capturer
                            </button>
                        </>
                    ) : (
                        <>
                            <button onClick={() => setCaptured(null)} className="flex-1 h-11 rounded-2xl border border-slate-300 text-slate-700 hover:bg-slate-50 font-medium flex items-center justify-center gap-2 text-sm transition">
                                <FiRefreshCw size={15} /> Reprendre
                            </button>
                            <button onClick={() => { onCapture(captured); onClose(); }} className="flex-1 h-11 rounded-2xl bg-blue-900 hover:bg-blue-800 text-white font-semibold flex items-center justify-center gap-2 text-sm transition shadow-lg shadow-blue-900/20">
                                <FiCheckCircle size={16} /> Utiliser
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const AddStaffModal = ({ onConfirm, onClose }) => {
    const [count, setCount] = useState(1);
    const set = (n) => setCount(Math.min(20, Math.max(1, n)));

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-white w-full sm:max-w-sm sm:rounded-3xl rounded-t-3xl overflow-hidden shadow-2xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-xl bg-blue-900/10 text-blue-900 flex items-center justify-center">
                            <FiUser size={16} />
                        </div>
                        <span className="font-semibold text-slate-800 text-sm">Ajouter des staffs</span>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center justify-center">
                        <FiX size={16} />
                    </button>
                </div>

                <div className="px-5 py-5">
                    <p className="text-sm text-slate-500 mb-4">Combien de membres souhaitez-vous ajouter ?</p>
                    <div className="flex items-center gap-3">
                        <button onClick={() => set(count - 1)} className="w-12 h-12 rounded-2xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xl font-bold flex items-center justify-center transition">−</button>
                        <input type="number" min={1} max={20} value={count} onChange={(e) => set(parseInt(e.target.value) || 1)} className="flex-1 h-12 rounded-2xl border border-slate-300 text-center text-xl font-bold text-blue-900 outline-none focus:border-blue-900 focus:ring-4 focus:ring-blue-100 transition" />
                        <button onClick={() => set(count + 1)} className="w-12 h-12 rounded-2xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xl font-bold flex items-center justify-center transition">+</button>
                    </div>
                    <p className="text-xs text-slate-400 text-center mt-2">Maximum 20 staffs</p>
                </div>

                <div className="px-5 pb-6 flex gap-3">
                    <button onClick={onClose} className="flex-1 h-11 rounded-2xl border border-slate-300 text-slate-700 hover:bg-slate-50 font-medium text-sm transition">Annuler</button>
                    <button onClick={() => { onConfirm(count); onClose(); }} className="flex-1 h-11 rounded-2xl bg-blue-900 hover:bg-blue-800 text-white font-semibold text-sm transition shadow-lg shadow-blue-900/20">
                        Ajouter {count > 1 ? `${count} staffs` : "1 staff"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const UploadBox = ({ title, file, onChange, side }) => {
    const [cameraOpen, setCameraOpen] = useState(false);

    return (
        <>
            {cameraOpen && <CameraModal onCapture={onChange} onClose={() => setCameraOpen(false)} />}

            <div className="rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden">
                <div className="px-3 sm:px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-blue-900/10 text-blue-900 flex items-center justify-center shrink-0">
                        {side === "recto" ? <FiCreditCard size={15} /> : <FiShield size={15} />}
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs font-semibold text-slate-700 truncate">{title}</p>
                        <p className="text-xs text-slate-400">JPG, JPEG, PNG</p>
                    </div>
                    {file && <FiCheckCircle className="text-green-500 ml-auto shrink-0" size={16} />}
                </div>

                <div className="p-3">
                    {file ? (
                        <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-white">
                            <img src={URL.createObjectURL(file)} alt={title} className="w-full object-cover" style={{ maxHeight: 110 }} />
                            <button onClick={() => onChange(null)} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-lg bg-black/50 text-white flex items-center justify-center">
                                <FiX size={12} />
                            </button>
                        </div>
                    ) : (
                        <div className="border-2 border-dashed border-slate-300 rounded-xl bg-white p-3 hover:border-blue-900 transition-all">
                            <div className="flex flex-col items-center text-center mb-3">
                                <div className="w-10 h-10 rounded-xl bg-blue-900/8 flex items-center justify-center text-blue-900 mb-1.5">
                                    <FiUpload size={18} />
                                </div>
                                <p className="text-xs text-slate-400">Caméra ou galerie</p>
                            </div>
                            <div className="flex gap-2">
                                {isMobile ? (
                                    <label className="flex-1 h-9 rounded-xl bg-blue-900 hover:bg-blue-800 text-white text-xs font-medium flex items-center justify-center gap-1 cursor-pointer transition">
                                        <FiCamera size={13} /> Photo
                                        <input type="file" accept="image/*" capture="environment" hidden onClick={(e) => { e.target.value = null; }} onChange={(e) => onChange(e.target.files?.[0])} />
                                    </label>
                                ) : (
                                    <button type="button" onClick={() => setCameraOpen(true)} className="flex-1 h-9 rounded-xl bg-blue-900 hover:bg-blue-800 text-white text-xs font-medium flex items-center justify-center gap-1 transition">
                                        <FiCamera size={13} /> Caméra
                                    </button>
                                )}
                                <label className="flex-1 h-9 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium flex items-center justify-center gap-1 cursor-pointer transition">
                                    <FiUpload size={13} /> Fichier
                                    <input type="file" accept="image/*" hidden onClick={(e) => { e.target.value = null; }} onChange={(e) => onChange(e.target.files?.[0])} />
                                </label>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

const BadgeTypeSelector = ({ value, onChange }) => (
    <div>
        <label className="text-xs font-semibold text-blue-900 mb-2 flex items-center gap-1.5">
            <FiTag size={12} /> Type *
        </label>
        <div className="inline-flex w-full rounded-xl border border-slate-200 bg-slate-50 p-1 gap-1">
            {BADGE_TYPES.map((t) => {
                const isActive = value === t.value;
                return (
                    <button
                        key={t.value}
                        type="button"
                        onClick={() => onChange(t.value)}
                        className={`flex-1 h-8 rounded-lg text-xs font-medium transition-all ${isActive
                            ? "bg-white text-blue-900 shadow-sm border border-slate-200 font-semibold"
                            : "text-slate-500 hover:text-slate-700"
                            }`}
                    >
                        {t.label}
                    </button>
                );
            })}
        </div>
        {!value && (
            <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1">
                <FiInfo size={11} /> Sélectionnez un type d'accès
            </p>
        )}
    </div>
);

const emptyStaff = (isStandiste) => ({
    name: "", cin: "", phone: "",
    type: isStandiste ? "" : "contractor",
    cinPhotos: { recto: null, verso: null },
    verification: { status: "idle", cinMatch: null, nameMatch: null },
});

// Central place that decides what the verification badge looks like for
// a given staff entry. Keeps the render code below simple.
const getVerificationDisplay = (staff) => {
    const { status, cinMatch, nameMatch } = staff.verification;

    if (status === "checking") {
        return {
            wrapperClass: "bg-blue-50 border-blue-100",
            icon: <FiRefreshCw size={13} className="text-blue-700 animate-spin shrink-0" />,
            textClass: "text-blue-700",
            text: "Vérification en cours...",
        };
    }
    if (status === "error") {
        return {
            wrapperClass: "bg-amber-50 border-amber-200",
            icon: <FiInfo size={13} className="text-amber-600 shrink-0" />,
            textClass: "text-amber-700",
            text: "Vérification indisponible — réessayez",
        };
    }
    if (status === "done") {
        if (cinMatch && nameMatch) {
            return {
                wrapperClass: "bg-green-50 border-green-200",
                icon: <FiCheckCircle size={13} className="text-green-600 shrink-0" />,
                textClass: "text-green-700",
                text: "Infos vérifiées avec la photo",
            };
        }
        return {
            wrapperClass: "bg-red-50 border-red-200",
            icon: <FiX size={13} className="text-red-500 shrink-0" />,
            textClass: "text-red-600",
            text: !cinMatch && !nameMatch
                ? "Nom et CIN ne correspondent pas à la photo — vérifiez ou reprenez la photo"
                : !cinMatch
                    ? "CIN ne correspond pas à la photo — vérifiez le numéro saisi"
                    : "Nom ne correspond pas à la photo — vérifiez l'orthographe",
        };
    }
    // status === "idle" but all fields are present — waiting on the
    // debounce timer, or waiting for the user to trigger a recheck.
    return {
        wrapperClass: "bg-slate-50 border-slate-200",
        icon: <FiInfo size={13} className="text-slate-400 shrink-0" />,
        textClass: "text-slate-500",
        text: "En attente de vérification...",
    };
};

const CreateBadge = () => {
    const [submitting, setSubmitting] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [user, setUser] = useState(null);
    const [companyName, setCompanyName] = useState("");

    const isStandiste = user?.role === "standiste";

    // Always-current snapshot of staffList, so debounced callbacks never
    // read stale data captured in an old render's closure.
    const staffListRef = useRef(staffList);
    useEffect(() => { staffListRef.current = staffList; }, [staffList]);

    // One debounce timer per staff index.
    const debounceTimers = useRef({});
    useEffect(() => () => {
        Object.values(debounceTimers.current).forEach(clearTimeout);
    }, []);

    const verifyCinPhoto = async (file, name, cin) => {
        const form = new FormData();
        form.append("recto", file);
        form.append("name", name);
        form.append("cin", cin);
        const { data } = await axiosInstance.post("/badge/verify-cin", form, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return data;
    };

    const runVerification = useCallback(async (index, file, name, cin) => {
        if (!file || !name || !cin) return;
        setStaffList((prev) => {
            const u = [...prev];
            if (!u[index]) return prev;
            u[index] = { ...u[index], verification: { status: "checking", cinMatch: null, nameMatch: null } };
            return u;
        });
        try {
            const result = await verifyCinPhoto(file, name, cin);
            setStaffList((prev) => {
                const u = [...prev];
                if (!u[index]) return prev;
                u[index] = {
                    ...u[index],
                    verification: { status: "done", cinMatch: result.cinMatch, nameMatch: result.nameMatch },
                };
                return u;
            });
        } catch {
            setStaffList((prev) => {
                const u = [...prev];
                if (!u[index]) return prev;
                u[index] = { ...u[index], verification: { status: "error", cinMatch: null, nameMatch: null } };
                return u;
            });
            toast.error(`Vérification CIN échouée pour staff #${index + 1}`);
        }
    }, []);

    // Debounced trigger: waits for a pause in typing/uploads, then reads
    // the LATEST staff data (via ref, not a stale closure) and only fires
    // the request if name + cin + photo are all actually present.
    const scheduleVerification = useCallback((index) => {
        if (debounceTimers.current[index]) clearTimeout(debounceTimers.current[index]);
        debounceTimers.current[index] = setTimeout(() => {
            const staff = staffListRef.current[index];
            if (!staff) return;
            const { name, cin, cinPhotos } = staff;
            if (cinPhotos.recto && name.trim() && cin.trim()) {
                runVerification(index, cinPhotos.recto, name.trim(), cin.trim());
            }
        }, VERIFY_DEBOUNCE_MS);
    }, [runVerification]);

    // Manual "Recheck" button — bypasses the debounce and checks immediately.
    const handleRecheck = useCallback((index) => {
        if (debounceTimers.current[index]) clearTimeout(debounceTimers.current[index]);
        const staff = staffListRef.current[index];
        if (!staff) return;
        const { name, cin, cinPhotos } = staff;
        if (cinPhotos.recto && name.trim() && cin.trim()) {
            runVerification(index, cinPhotos.recto, name.trim(), cin.trim());
        }
    }, [runVerification]);

    useEffect(() => {
        if (user) setStaffList([emptyStaff(isStandiste)]);
    }, [user]);

    const handleAddConfirm = (count) =>
        setStaffList((prev) => [...prev, ...Array.from({ length: count }, () => emptyStaff(isStandiste))]);

    const removeStaff = (index) => {
        if (debounceTimers.current[index]) clearTimeout(debounceTimers.current[index]);
        setStaffList((prev) => prev.filter((_, i) => i !== index));
    };

    const updateStaffPhoto = (index, side, file) => {
        setStaffList((prev) => {
            const u = [...prev];
            u[index] = {
                ...u[index],
                cinPhotos: { ...u[index].cinPhotos, [side]: file },
                // Clear any previous badge immediately so a stale result
                // never lingers after the photo changes.
                verification: side === "recto"
                    ? { status: "idle", cinMatch: null, nameMatch: null }
                    : u[index].verification,
            };
            return u;
        });
        if (side === "recto") scheduleVerification(index);
    };

    const updateStaff = (index, field, value) => {
        setStaffList((prev) => {
            const u = [...prev];
            u[index] = {
                ...u[index],
                [field]: value,
                verification: (field === "name" || field === "cin")
                    ? { status: "idle", cinMatch: null, nameMatch: null }
                    : u[index].verification,
            };
            return u;
        });
        if (field === "name" || field === "cin") scheduleVerification(index);
    };

    // ---- Verification summary across all staff, used to drive the submit button ----
    const verificationSummary = (() => {
        let checking = 0, failed = 0, notVerified = 0, ok = 0;
        for (const s of staffList) {
            const hasRecto = !!s.cinPhotos.recto;
            const canVerify = hasRecto && s.name.trim() && s.cin.trim();
            if (!canVerify) { notVerified++; continue; }
            if (s.verification.status === "checking") checking++;
            else if (s.verification.status === "done" && s.verification.cinMatch && s.verification.nameMatch) ok++;
            else failed++; // idle-pending, done-with-mismatch, or error
        }
        return { checking, failed, notVerified, ok, total: staffList.length };
    })();

    const isVerifying = verificationSummary.checking > 0;
    const hasUnverified = verificationSummary.failed > 0 || verificationSummary.notVerified > 0;

    const handleSubmit = async () => {
        if (submitting) return;

        for (let i = 0; i < staffList.length; i++) {
            const p = staffList[i];
            if (!p.name || !p.cin || !p.phone) {
                toast.error(`Remplissez tous les champs du staff #${i + 1}`);
                return;
            }
            if (isStandiste && !p.type) {
                toast.error(`Sélectionnez un type d'accès pour le staff #${i + 1}`);
                return;
            }
            if (!p.cinPhotos.recto || !p.cinPhotos.verso) {
                toast.error(`Ajoutez les photos CIN du staff #${i + 1}`);
                return;
            }
            if (p.verification.status === "checking") {
                toast.error(`Vérification en cours pour le staff #${i + 1}, patientez...`);
                return;
            }
            if (p.verification.status !== "done" || !p.verification.cinMatch || !p.verification.nameMatch) {
                toast.error(`Vérification CIN non confirmée pour le staff #${i + 1} — corrigez ou réessayez`);
                return;
            }
        }

        try {
            setSubmitting(true);
            const managerId = localStorage.getItem("userId");
            const form = new FormData();

            form.append("badges", JSON.stringify(
                staffList.map(({ name, cin, phone, type }) => ({ name, cin, phone, type }))
            ));
            staffList.forEach((s, i) => {
                form.append(`badges[${i}][cinPhotos]`, s.cinPhotos.recto);
                form.append(`badges[${i}][cinPhotos]`, s.cinPhotos.verso);
            });

            const { data } = await axiosInstance.post(`/badge/staff/${managerId}`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (data.duplicates?.length > 0) {
                data.duplicates.forEach((d) => {
                    toast.error(`${d.name} (CIN ${d.cin}) a déjà un badge actif`);
                });
            }
            if (data.created > 0) {
                toast.success(
                    `${data.created} badge${data.created > 1 ? "s" : ""} créé${data.created > 1 ? "s" : ""} avec succès`
                );
            }
            setStaffList([emptyStaff(isStandiste)]);
        } catch (err) {
            const data = err?.response?.data;
            const duplicates = data?.duplicates;

            if (duplicates?.length > 0) {
                duplicates.forEach((d) => {
                    toast.error(`${d.name} (CIN ${d.cin}) a déjà un badge actif`);
                });
            } else {
                toast.error(data?.message || "Erreur lors de la création");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const formatRole = (r) => {
        if (r === "standiste") return "Stand Builder";
        if (r === "contractor") return "Contractor";
        return r ? r.charAt(0).toUpperCase() + r.slice(1) : "—";
    };

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const userId = localStorage.getItem("userId");
                const { data } = await axiosInstance.get(`/auth/user/${userId}`);
                setUser(data.user);
                const contractor = data.user?.stands?.[0]?.onsite?.contractor;
                setCompanyName(contractor?.companyName || "");
            } catch (err) {
                console.error(err);
            }
        };
        fetchUser();
    }, []);

    const inputFields = [
        { field: "name", label: "Nom complet", placeholder: "Nom complet", icon: FiUser },
        { field: "phone", label: "Téléphone", placeholder: "Téléphone", icon: FiPhone },
        { field: "cin", label: "CIN / Passport", placeholder: "CIN / Passport", icon: FiCreditCard, hint: "Un seul badge actif par CIN autorisé." },
    ];

    // ---- Submit button label/state, computed once ----
    const submitLabel = (() => {
        if (submitting) return "Création en cours...";
        if (isVerifying) return "Vérification en cours...";
        if (hasUnverified) return "Vérification requise";
        return `Créer ${staffList.length > 1 ? `${staffList.length} badges` : "le badge"}`;
    })();
    const submitDisabled = submitting || isVerifying;
    const submitLocked = hasUnverified && !isVerifying && !submitting;

    return (
        <>
            <Navbar />
            {addModalOpen && (
                <AddStaffModal onConfirm={handleAddConfirm} onClose={() => setAddModalOpen(false)} />
            )}

            {/* MAIN CONTAINER */}
            <div className="min-h-screen bg-[#f5f3ee] px-2 py-4 sm:px-4 sm:py-6 lg:px-6 lg:py-8">
                <div className="max-w-7xl mx-auto">

                    {/* HEADER */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6">
                        <div className="min-w-0">
                            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blue-900 leading-tight">
                                Créer un badge
                            </h1>

                            <p className="text-xs sm:text-sm text-slate-500 mt-1">
                                Ajouter les membres de votre équipe
                            </p>
                        </div>

                        <button
                            onClick={() => setAddModalOpen(true)}
                            className="w-full sm:w-auto h-11 sm:h-12 px-4 sm:px-5 rounded-2xl bg-blue-900 hover:bg-blue-800 transition text-white flex items-center justify-center gap-2 font-medium text-sm shadow-lg shadow-blue-900/20 shrink-0"
                        >
                            <FiPlus size={17} />

                            <span className="hidden sm:inline">
                                Ajouter un staff
                            </span>

                            <span className="sm:hidden">
                                Ajouter un staff
                            </span>
                        </button>
                    </div>

                    {/* CONTENT */}
                    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-4 lg:gap-5">

                        {/* LEFT */}
                        <div className="space-y-4 min-w-0">
                            {staffList.map((staff, index) => {
                                const hasAllFields = !!staff.cinPhotos.recto && staff.name.trim() && staff.cin.trim();
                                const display = hasAllFields ? getVerificationDisplay(staff) : null;
                                const canRecheck = hasAllFields && staff.verification.status !== "checking";

                                return (
                                    <div
                                        key={index}
                                        className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden"
                                    >
                                        {/* CARD HEADER */}
                                        <div className="px-4 sm:px-5 py-3.5 border-b border-slate-100 flex items-center justify-between gap-3">
                                            <div className="min-w-0">
                                                <h2 className="text-sm sm:text-base font-semibold text-blue-900 truncate">
                                                    Staff #{index + 1}
                                                </h2>

                                                <p className="text-xs text-slate-400 mt-0.5">
                                                    Informations de la personne
                                                </p>
                                            </div>

                                            {staffList.length > 1 && (
                                                <button
                                                    onClick={() =>
                                                        removeStaff(index)
                                                    }
                                                    className="w-9 h-9 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 transition flex items-center justify-center shrink-0"
                                                >
                                                    <FiTrash2 size={14} />
                                                </button>
                                            )}
                                        </div>

                                        {/* CARD BODY */}
                                        <div className="p-4 sm:p-5 space-y-4">

                                            {/* INPUTS */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                                                {inputFields.map(
                                                    ({
                                                        field,
                                                        label,
                                                        placeholder,
                                                        icon: Icon,
                                                        hint,
                                                    }) => (
                                                        <div
                                                            key={field}
                                                            className={
                                                                field === "cin"
                                                                    ? "sm:col-span-2 xl:col-span-1"
                                                                    : ""
                                                            }
                                                        >
                                                            <label className="text-xs font-semibold text-blue-900 mb-1.5 block">
                                                                {label} *
                                                            </label>

                                                            <div className="relative">
                                                                <Icon
                                                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                                                    size={14}
                                                                />

                                                                <input
                                                                    type="text"
                                                                    value={
                                                                        staff[
                                                                        field
                                                                        ]
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateStaff(
                                                                            index,
                                                                            field,
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    placeholder={
                                                                        placeholder
                                                                    }
                                                                    className="w-full h-11 sm:h-12 rounded-2xl border border-slate-300 bg-white pl-9 pr-3 text-sm outline-none focus:border-blue-900 focus:ring-4 focus:ring-blue-100 transition"
                                                                />
                                                            </div>

                                                            {hint && (
                                                                <div className="flex items-start gap-1 mt-1.5 text-xs text-slate-400">
                                                                    <FiInfo
                                                                        className="mt-px shrink-0"
                                                                        size={11}
                                                                    />

                                                                    <span>
                                                                        {
                                                                            hint
                                                                        }
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )
                                                )}
                                            </div>

                                            {/* BADGE TYPE */}
                                            {isStandiste && (
                                                <BadgeTypeSelector
                                                    value={staff.type}
                                                    onChange={(v) =>
                                                        updateStaff(
                                                            index,
                                                            "type",
                                                            v
                                                        )
                                                    }
                                                />
                                            )}

                                            {/* VERIFICATION STATUS + RECHECK */}
                                            {display && (
                                                <div className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl border transition-colors duration-300 ${display.wrapperClass}`}>
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        {display.icon}
                                                        <span className={`text-xs font-medium truncate ${display.textClass}`}>
                                                            {display.text}
                                                        </span>
                                                    </div>
                                                    {canRecheck && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRecheck(index)}
                                                            className="shrink-0 flex items-center gap-1 text-xs font-semibold text-blue-900 hover:text-blue-700 transition px-2 py-1 rounded-lg hover:bg-white/60"
                                                        >
                                                            <FiRefreshCw size={11} /> Revérifier
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                            {/* UPLOADS */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <UploadBox
                                                    title="CIN Recto"
                                                    file={
                                                        staff
                                                            .cinPhotos
                                                            .recto
                                                    }
                                                    side="recto"
                                                    onChange={(f) =>
                                                        updateStaffPhoto(
                                                            index,
                                                            "recto",
                                                            f
                                                        )
                                                    }
                                                />

                                                <UploadBox
                                                    title="CIN Verso"
                                                    file={
                                                        staff
                                                            .cinPhotos
                                                            .verso
                                                    }
                                                    side="verso"
                                                    onChange={(f) =>
                                                        updateStaffPhoto(
                                                            index,
                                                            "verso",
                                                            f
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* RIGHT SIDEBAR */}
                        <div className="space-y-4 xl:sticky xl:top-24 h-fit">

                            {/* SETTINGS */}
                            <div className="bg-white rounded-3xl border border-slate-200 p-4 sm:p-5 shadow-sm">
                                <div className="flex items-center gap-2 mb-3">
                                    <FiShield
                                        className="text-blue-900 shrink-0"
                                        size={15}
                                    />

                                    <h2 className="text-sm font-semibold text-blue-900">
                                        Paramètres verrouillés
                                    </h2>
                                </div>

                                <p className="text-xs text-slate-500 leading-relaxed mb-4">
                                    Définis par l'administrateur,
                                    non modifiables.
                                </p>

                                <div className="space-y-4">
                                    <div>
                                        <p className="text-xs text-slate-400 mb-1">
                                            Société
                                        </p>

                                        <span className="inline-flex max-w-full px-2.5 py-1 rounded-xl bg-slate-100 text-slate-700 text-xs font-medium break-all">
                                            {companyName || "—"}
                                        </span>
                                    </div>

                                    <div>
                                        <p className="text-xs text-slate-400 mb-1">
                                            Rôle
                                        </p>

                                        <p className="text-xs font-medium text-slate-700">
                                            {formatRole(
                                                user?.role
                                            )}
                                        </p>
                                    </div>

                                    {isStandiste && (
                                        <div>
                                            <p className="text-xs text-slate-400 mb-2">
                                                Types d'accès
                                            </p>

                                            <div className="flex flex-wrap gap-2">
                                                {BADGE_TYPES.map(
                                                    (t) => (
                                                        <span
                                                            key={
                                                                t.value
                                                            }
                                                            className="px-2.5 py-1 rounded-xl bg-blue-50 text-blue-900 text-xs font-medium"
                                                        >
                                                            {
                                                                t.label
                                                            }
                                                        </span>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* RULE */}
                            <div className="bg-white rounded-3xl border border-slate-200 p-4 sm:p-5 shadow-sm">
                                <div className="flex items-center gap-2 mb-3">
                                    <FiInfo
                                        className="text-blue-900 shrink-0"
                                        size={15}
                                    />

                                    <h2 className="text-sm font-semibold text-blue-900">
                                        Règle importante
                                    </h2>
                                </div>

                                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3">
                                    <p className="text-xs leading-relaxed text-amber-800">
                                        Un CIN / Passeport ne peut
                                        avoir qu'un seul badge actif
                                        à la fois.
                                    </p>
                                </div>
                            </div>

                            {/* VERIFICATION SUMMARY */}
                            {(isVerifying || hasUnverified) && (
                                <div className={`rounded-3xl border p-4 sm:p-5 shadow-sm transition-colors duration-300 ${isVerifying ? "bg-blue-50 border-blue-200" : "bg-slate-50 border-slate-200"
                                    }`}>
                                    <div className="flex items-center gap-2">
                                        {isVerifying ? (
                                            <FiRefreshCw size={14} className="text-blue-700 animate-spin shrink-0" />
                                        ) : (
                                            <FiLock size={14} className="text-slate-400 shrink-0" />
                                        )}
                                        <p className={`text-xs font-medium ${isVerifying ? "text-blue-700" : "text-slate-500"}`}>
                                            {isVerifying
                                                ? "Vérification CIN en cours, patientez..."
                                                : "Certains staffs n'ont pas encore été vérifiés"}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* SUBMIT */}
                            <button
                                onClick={handleSubmit}
                                disabled={submitDisabled}
                                className={`w-full py-3.5 sm:py-4 rounded-2xl text-sm font-semibold shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:cursor-not-allowed ${submitLocked
                                    ? "bg-slate-200 text-slate-400 shadow-none hover:bg-slate-300"
                                    : "bg-blue-900 hover:bg-blue-800 text-white shadow-blue-900/20 disabled:opacity-70"
                                    }`}
                            >
                                {(submitting || isVerifying) && (
                                    <FiRefreshCw size={15} className="animate-spin" />
                                )}
                                {submitLocked && <FiLock size={14} />}
                                {submitLabel}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CreateBadge;